import React, { useState, useMemo, useRef } from 'react';
import { 
  MessageSquare, Plus, Trash2, Edit3, Save, Check, Sparkles, 
  Send, Smartphone, Copy, RefreshCw, Zap, Bell, CheckCircle2, 
  HelpCircle, Eye, AlertCircle, PlayCircle, ShieldCheck
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useAuthStore } from '../store/useAuthStore';
import { DEMO_MESSAGE_TEMPLATES } from '../utils/demoData';
import { setStoredFeatureOverride } from '../utils/businessConfigHelpers';
import toast from 'react-hot-toast';

const AVAILABLE_VARIABLES = [
  { tag: '{{nombre}}', label: 'Nombre Cliente', desc: 'Nombre del destinatario', sample: 'Carlos Mendoza' },
  { tag: '{{negocio}}', label: 'Nombre Negocio', desc: 'Tu marca comercial', sample: 'SeryiFlow' },
  { tag: '{{servicio}}', label: 'Servicio / Plato', desc: 'Servicio o producto solicitado', sample: 'Corte Fade & Barba' },
  { tag: '{{fecha}}', label: 'Fecha', desc: 'Día de la cita o pedido', sample: 'Mañana, 15 de Octubre' },
  { tag: '{{hora}}', label: 'Hora', desc: 'Hora pactada', sample: '04:30 PM' },
  { tag: '{{precio}}', label: 'Precio', desc: 'Monto total o tarifa', sample: '$45.000 COP' },
  { tag: '{{telefono}}', label: 'Teléfono', desc: 'Número del cliente', sample: '+57 300 123 4567' }
];

const DEFAULT_TRIGGERS = [
  'Primer contacto por WhatsApp (Bienvenida)',
  '2 horas antes de la cita (Recordatorio)',
  'Al confirmar reserva o pedido',
  '20 días sin interacción (Recuperación)',
  'Disparo manual desde Campañas',
  'Al finalizar servicio (Solicitud de Reseña)'
];

const CATEGORIES = ['Atención al Cliente', 'Agenda', 'Transaccional', 'Fidelización', 'Promocional'];

export default function MessageTemplatesManager({ activeBusiness, onUpdateBusiness, isReadOnlyDemo }) {
  const { userRole } = useAuthStore();
  const textareaRef = useRef(null);

  // Inicializar plantillas desde negocio o usar presets demo
  const initialTemplates = useMemo(() => {
    if (Array.isArray(activeBusiness?.message_templates) && activeBusiness.message_templates.length > 0) {
      return activeBusiness.message_templates;
    }
    return DEMO_MESSAGE_TEMPLATES;
  }, [activeBusiness]);

  const [templates, setTemplates] = useState(initialTemplates);
  const [selectedTemplateId, setSelectedTemplateId] = useState(initialTemplates[0]?.id || 'tpl-bienvenida');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form state para la plantilla activa
  const activeTemplate = useMemo(() => {
    return templates.find(t => t.id === selectedTemplateId) || templates[0] || null;
  }, [templates, selectedTemplateId]);

  const [editNombre, setEditNombre] = useState(activeTemplate?.nombre || '');
  const [editTrigger, setEditTrigger] = useState(activeTemplate?.trigger || DEFAULT_TRIGGERS[0]);
  const [editCategoria, setEditCategoria] = useState(activeTemplate?.categoria || CATEGORIES[0]);
  const [editContenido, setEditContenido] = useState(activeTemplate?.contenido || '');
  const [editActivo, setEditActivo] = useState(activeTemplate?.activo !== false);

  // Sincronizar formulario al cambiar selección
  React.useEffect(() => {
    if (activeTemplate) {
      setEditNombre(activeTemplate.nombre || '');
      setEditTrigger(activeTemplate.trigger || DEFAULT_TRIGGERS[0]);
      setEditCategoria(activeTemplate.categoria || CATEGORIES[0]);
      setEditContenido(activeTemplate.contenido || '');
      setEditActivo(activeTemplate.activo !== false);
      setIsCreatingNew(false);
    }
  }, [selectedTemplateId, activeTemplate]);

  // Insertar variable en el cursor del textarea
  const handleInsertVariable = (tag) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setEditContenido(prev => prev + ' ' + tag);
      return;
    }

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const current = editContenido;
    const updated = current.substring(0, start) + tag + current.substring(end);
    setEditContenido(updated);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + tag.length, start + tag.length);
    }, 50);
  };

  // Guardar plantilla (Persistencia Supabase + Local)
  const handleSaveTemplate = async () => {
    if (!editNombre.trim() || !editContenido.trim()) {
      toast.error("Por favor completa el título y el mensaje de la plantilla.");
      return;
    }

    setIsSaving(true);
    const updatedTemplate = {
      id: isCreatingNew ? `tpl-${Date.now()}` : activeTemplate.id,
      nombre: editNombre.trim(),
      trigger: editTrigger,
      categoria: editCategoria,
      contenido: editContenido.trim(),
      activo: editActivo,
      updated_at: new Date().toISOString()
    };

    let nextTemplates;
    if (isCreatingNew) {
      nextTemplates = [updatedTemplate, ...templates];
      setSelectedTemplateId(updatedTemplate.id);
      setIsCreatingNew(false);
    } else {
      nextTemplates = templates.map(t => t.id === updatedTemplate.id ? updatedTemplate : t);
    }

    setTemplates(nextTemplates);

    // 1. Actualizar estado del negocio localmente
    if (onUpdateBusiness && activeBusiness) {
      onUpdateBusiness({
        ...activeBusiness,
        message_templates: nextTemplates
      });
    }

    // 2. Guardar en Storage overrides
    if (activeBusiness?.id) {
      setStoredFeatureOverride(activeBusiness.id, 'message_templates', nextTemplates);
    }

    // 3. Persistir en Supabase
    if (!isReadOnlyDemo && activeBusiness?.id) {
      try {
        const { error } = await supabase
          .from('businesses')
          .update({ message_templates: nextTemplates })
          .eq('id', activeBusiness.id);

        if (error) {
          console.warn("Aviso al guardar plantillas en Supabase:", error.message);
        }
        toast.success("Plantilla guardada y sincronizada correctamente");
      } catch (err) {
        console.error("Error guardando plantilla:", err);
        toast.success("Plantilla guardada localmente");
      }
    } else {
      toast.success("Plantilla guardada (Simulación Demo)");
    }

    setIsSaving(false);
  };

  // Eliminar plantilla
  const handleDeleteTemplate = async (idToDelete) => {
    if (templates.length <= 1) {
      toast.error("Debes mantener al menos una plantilla base en el sistema.");
      return;
    }

    const nextTemplates = templates.filter(t => t.id !== idToDelete);
    setTemplates(nextTemplates);
    setSelectedTemplateId(nextTemplates[0].id);

    if (onUpdateBusiness && activeBusiness) {
      onUpdateBusiness({
        ...activeBusiness,
        message_templates: nextTemplates
      });
    }

    if (activeBusiness?.id) {
      setStoredFeatureOverride(activeBusiness.id, 'message_templates', nextTemplates);
    }

    if (!isReadOnlyDemo && activeBusiness?.id) {
      try {
        await supabase
          .from('businesses')
          .update({ message_templates: nextTemplates })
          .eq('id', activeBusiness.id);
      } catch (e) {}
    }

    toast.success("Plantilla eliminada");
  };

  // Crear nueva plantilla
  const handleStartNew = () => {
    setIsCreatingNew(true);
    setEditNombre('Nueva Plantilla Automatizada');
    setEditTrigger(DEFAULT_TRIGGERS[0]);
    setEditCategoria(CATEGORIES[0]);
    setEditContenido('¡Hola {{nombre}}! 👋 Te escribimos de *{{negocio}}* para...');
    setEditActivo(true);
  };

  // Mensaje renderizado para la vista previa en tiempo real
  const previewMessage = useMemo(() => {
    let text = editContenido || '';
    AVAILABLE_VARIABLES.forEach(v => {
      const sampleVal = v.tag === '{{negocio}}' ? (activeBusiness?.nombre_negocio || v.sample) : v.sample;
      text = text.split(v.tag).join(sampleVal);
    });
    return text;
  }, [editContenido, activeBusiness]);

  // Probar en WhatsApp personal
  const handleTestWhatsApp = () => {
    const encoded = encodeURIComponent(previewMessage);
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Cabecera del Editor */}
      <div className="bg-seryi-bg border border-seryi-bg-light p-6 md:p-8 rounded-3xl shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-seryi-green/5 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>
        <div className="relative z-10 space-y-1 max-w-2xl">
          <div className="flex items-center gap-2 text-seryi-green text-xs font-bold uppercase tracking-widest">
            <MessageSquare size={16} />
            <span>Editor de Mensajes & Automatización IA</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-seryi-heading tracking-tight">
            Plantillas Dinámicas para WhatsApp
          </h2>
          <p className="text-xs md:text-sm text-seryi-text">
            Diseña los mensajes que tu Asistente IA y flujos de automatización enviarán automáticamente a tus clientes. Personaliza con variables en tiempo real.
          </p>
        </div>

        <button
          onClick={handleStartNew}
          className="bg-seryi-green hover:brightness-110 text-seryi-bg-dark font-extrabold px-5 py-3 rounded-2xl text-xs flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 shrink-0"
        >
          <Plus size={16} />
          <span>Nueva Plantilla</span>
        </button>
      </div>

      {/* Cuerpo: Lista lateral + Editor central + Preview WhatsApp */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* COLUMNA 1: Selector de Plantillas (3 cols) */}
        <div className="lg:col-span-4 bg-seryi-bg border border-seryi-bg-light rounded-3xl p-4 md:p-5 space-y-3">
          <div className="flex items-center justify-between px-1 pb-2 border-b border-seryi-bg-light/60">
            <span className="text-xs font-bold uppercase tracking-wider text-seryi-text">Plantillas Guardadas</span>
            <span className="text-[10px] font-bold bg-seryi-bg-dark px-2 py-0.5 rounded-full text-seryi-green border border-seryi-bg-light">
              {templates.length}
            </span>
          </div>

          <div className="space-y-2 max-h-[550px] overflow-y-auto pr-1">
            {templates.map(tpl => {
              const isSelected = !isCreatingNew && selectedTemplateId === tpl.id;
              return (
                <div
                  key={tpl.id}
                  onClick={() => {
                    setIsCreatingNew(false);
                    setSelectedTemplateId(tpl.id);
                  }}
                  className={`p-3.5 rounded-2xl border transition-all cursor-pointer text-left relative group ${
                    isSelected
                      ? 'bg-seryi-bg-light/40 border-seryi-green shadow-md shadow-seryi-green/10'
                      : 'bg-seryi-bg-dark border-seryi-bg-light hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h4 className={`text-xs font-bold truncate flex-1 ${isSelected ? 'text-seryi-green' : 'text-seryi-heading'}`}>
                      {tpl.nombre}
                    </h4>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${tpl.activo !== false ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                  </div>

                  <p className="text-[11px] text-seryi-text line-clamp-2 leading-relaxed mb-2 font-mono">
                    {tpl.contenido}
                  </p>

                  <div className="flex items-center justify-between text-[10px] text-slate-500 pt-2 border-t border-seryi-bg-light/40">
                    <span className="bg-seryi-bg px-2 py-0.5 rounded text-indigo-300 font-medium">
                      {tpl.categoria || 'General'}
                    </span>
                    <span className="truncate max-w-[120px] text-slate-400">
                      {tpl.trigger || 'Manual'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* COLUMNA 2: Formulario Editor & Chips (5 cols) */}
        <div className="lg:col-span-5 bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 md:p-6 space-y-5">
          <div className="flex items-center justify-between border-b border-seryi-bg-light/60 pb-3">
            <div>
              <h3 className="text-sm font-black text-seryi-heading flex items-center gap-2">
                <Edit3 size={15} className="text-seryi-green" />
                {isCreatingNew ? 'Crear Nueva Plantilla' : 'Configurar Plantilla'}
              </h3>
              <p className="text-[11px] text-seryi-text">Edita el texto, detonador y variables dinámicas.</p>
            </div>

            {!isCreatingNew && (
              <button
                onClick={() => handleDeleteTemplate(activeTemplate?.id)}
                className="text-rose-400 hover:text-rose-300 p-2 rounded-xl hover:bg-rose-500/10 transition-colors"
                title="Eliminar plantilla"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>

          <div className="space-y-4">
            {/* Nombre de la plantilla */}
            <div>
              <label className="text-[11px] font-bold text-seryi-heading uppercase tracking-wider block mb-1.5">
                Nombre Descriptivo
              </label>
              <input
                type="text"
                value={editNombre}
                onChange={(e) => setEditNombre(e.target.value)}
                placeholder="Ej: Recordatorio 2h Antes"
                className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3.5 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green transition-colors"
              />
            </div>

            {/* Categoría y Detonador */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold text-seryi-heading uppercase tracking-wider block mb-1.5">
                  Categoría
                </label>
                <select
                  value={editCategoria}
                  onChange={(e) => setEditCategoria(e.target.value)}
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green transition-colors"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold text-seryi-heading uppercase tracking-wider block mb-1.5">
                  Detonador de Flujo
                </label>
                <select
                  value={editTrigger}
                  onChange={(e) => setEditTrigger(e.target.value)}
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green transition-colors"
                >
                  {DEFAULT_TRIGGERS.map(trig => (
                    <option key={trig} value={trig}>{trig}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Inserción de Variables Dinámicas */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] font-bold text-seryi-heading uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles size={12} className="text-seryi-green" /> Variables Disponibles
                </label>
                <span className="text-[10px] text-seryi-text">Click para insertar</span>
              </div>
              <div className="flex flex-wrap gap-1.5 p-2.5 bg-seryi-bg-dark border border-seryi-bg-light rounded-2xl">
                {AVAILABLE_VARIABLES.map(v => (
                  <button
                    key={v.tag}
                    type="button"
                    onClick={() => handleInsertVariable(v.tag)}
                    className="bg-seryi-bg hover:bg-seryi-green/20 text-indigo-300 hover:text-seryi-green border border-seryi-bg-light hover:border-seryi-green/40 px-2.5 py-1 rounded-xl text-[11px] font-mono transition-all active:scale-95 flex items-center gap-1"
                    title={v.desc}
                  >
                    <span>{v.tag}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contenido del Mensaje */}
            <div>
              <label className="text-[11px] font-bold text-seryi-heading uppercase tracking-wider block mb-1.5">
                Cuerpo del Mensaje (WhatsApp Formatted)
              </label>
              <textarea
                ref={textareaRef}
                rows={6}
                value={editContenido}
                onChange={(e) => setEditContenido(e.target.value)}
                placeholder="Escribe el mensaje aquí. Usa *negrita*, _cursiva_ y variables como {{nombre}}..."
                className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-2xl p-3.5 text-xs text-seryi-heading font-sans focus:outline-none focus:border-seryi-green transition-colors resize-none leading-relaxed"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Tip: Usa asteriscos para resaltar en negrita: <code className="text-seryi-green">*texto*</code>
              </p>
            </div>

            {/* Toggle de Activación */}
            <div className="flex items-center justify-between p-3 bg-seryi-bg-dark border border-seryi-bg-light rounded-2xl">
              <div>
                <span className="text-xs font-bold text-seryi-heading block">Estado de la Automatización</span>
                <span className="text-[10px] text-seryi-text">Si está inactiva, la plataforma no disparará este mensaje.</span>
              </div>
              <button
                type="button"
                onClick={() => setEditActivo(!editActivo)}
                className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300 ${
                  editActivo ? 'bg-seryi-green justify-end' : 'bg-slate-700 justify-start'
                }`}
              >
                <div className={`w-4 h-4 rounded-full shadow-md ${editActivo ? 'bg-seryi-bg-dark' : 'bg-white'}`} />
              </button>
            </div>

            {/* Botón Guardar */}
            <button
              onClick={handleSaveTemplate}
              disabled={isSaving}
              className="w-full bg-seryi-green hover:brightness-110 text-seryi-bg-dark font-extrabold py-3.5 rounded-2xl text-xs flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95"
            >
              <Save size={16} />
              <span>{isSaving ? "Guardando Cambios..." : "Guardar y Activar Plantilla"}</span>
            </button>
          </div>
        </div>

        {/* COLUMNA 3: Previsualización Móvil WhatsApp (3 cols) */}
        <div className="lg:col-span-3 bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-seryi-bg-light/60 pb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-seryi-text flex items-center gap-1.5">
              <Smartphone size={15} className="text-seryi-green" /> Previsualización
            </span>
            <span className="text-[10px] text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
              En Vivo
            </span>
          </div>

          {/* Maqueta de Celular */}
          <div className="bg-[#0b141a] border-4 border-slate-800 rounded-[2.5rem] p-3 shadow-2xl space-y-3 relative overflow-hidden">
            {/* Barra superior de WhatsApp */}
            <div className="bg-[#1f2c34] -mx-3 -mt-3 p-3 flex items-center gap-2.5 border-b border-slate-800">
              <div className="w-8 h-8 rounded-full bg-seryi-green/20 border border-seryi-green/40 flex items-center justify-center text-seryi-green font-bold text-xs">
                {(activeBusiness?.nombre_negocio || 'SF')[0]}
              </div>
              <div className="flex-1 min-w-0">
                <h5 className="text-xs font-bold text-slate-200 truncate">
                  {activeBusiness?.nombre_negocio || 'Mi Negocio'}
                </h5>
                <p className="text-[9px] text-emerald-400">En línea (Asistente IA)</p>
              </div>
            </div>

            {/* Chat Bubble */}
            <div className="py-6 px-1 min-h-[220px] flex flex-col justify-end">
              <div className="bg-[#005c4b] text-slate-100 p-3 rounded-2xl rounded-tl-sm text-[11px] leading-relaxed shadow space-y-1 relative max-w-[95%]">
                <p className="whitespace-pre-wrap break-words font-sans">
                  {previewMessage || 'Escribe tu mensaje...'}
                </p>
                <div className="flex items-center justify-end gap-1 text-[9px] text-emerald-200/70 pt-1">
                  <span>12:45 PM</span>
                  <CheckCircle2 size={10} className="text-sky-300" />
                </div>
              </div>
            </div>

            {/* Botón Probar en WhatsApp */}
            <button
              onClick={handleTestWhatsApp}
              className="w-full bg-[#202c33] hover:bg-[#2a3942] text-emerald-400 border border-emerald-500/30 font-bold py-2 rounded-xl text-[11px] flex items-center justify-center gap-1.5 transition-colors"
            >
              <Send size={12} />
              <span>Probar en mi WhatsApp</span>
            </button>
          </div>

          <div className="p-3 bg-seryi-bg-dark border border-seryi-bg-light/60 rounded-2xl space-y-1">
            <div className="flex items-center gap-1.5 text-[11px] font-bold text-seryi-heading">
              <Zap size={13} className="text-amber-400" />
              <span>Integración Inteligente</span>
            </div>
            <p className="text-[10px] text-seryi-text leading-relaxed">
              Las plantillas activas son leídas en tiempo real por los motores de automatización para despachar notificaciones sin intervención humana.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
